"use client";

import { useState } from "react";
import Link from "next/link";
import { BookingModal } from "@/components/BookingModal";
import { ContactFooter } from "@/components/ContactFooter";
import type { ServiceInfo } from "@/lib/services";

export default function ServicePageClient({ service }: { service: ServiceInfo }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <BookingModal
        open={showModal}
        onOpenChange={setShowModal}
        defaultService={service.name}
      />

      <main className="min-h-screen">
        {/* pt-32 gives space below the fixed global header */}
        <section className="bg-gradient-to-b from-primary/10 to-background pt-32 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">
              SkinSio Clinics · Vadodara
            </p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {service.heading}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {service.description}
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-block bg-primary text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Book Consultation
            </button>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="max-w-4xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div className="p-6 rounded-2xl border bg-card">
            <p className="text-2xl font-bold text-primary mb-2">FDA Approved</p>
            <p className="text-muted-foreground text-sm">US FDA-approved technology for safe, effective treatments</p>
          </div>
          <div className="p-6 rounded-2xl border bg-card">
            <p className="text-2xl font-bold text-primary mb-2">5★ Rated</p>
            <p className="text-muted-foreground text-sm">2200+ Happy Patients with 5-star Google reviews</p>
          </div>
          <div className="p-6 rounded-2xl border bg-card">
            <p className="text-2xl font-bold text-primary mb-2">Expert Doctors</p>
            <p className="text-muted-foreground text-sm">Experienced Doctors, Plastic Surgeons and certified aesthetic specialists</p>
          </div>
        </section>

        {/* Mid-page CTA before the long-form content */}
        {service.content && (
          <section className="max-w-3xl mx-auto px-4 text-center pb-4">
            <button
              onClick={() => setShowModal(true)}
              className="inline-block bg-primary text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Book Your Consultation
            </button>
          </section>
        )}

        {/* Rich long-form content (16-section expanded pages) */}
        {service.content && (
          <section className="max-w-3xl mx-auto px-4 py-8">
            <div
              className="text-muted-foreground leading-relaxed [&_p]:mb-4"
              dangerouslySetInnerHTML={{ __html: service.content }}
            />
          </section>
        )}

        {/* FAQ Section */}
        <section className="max-w-3xl mx-auto px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {service.faqs.map((faq, index) => (
              <details key={index} className="group border rounded-2xl bg-card overflow-hidden">
                <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer font-semibold text-base list-none hover:bg-primary/5 transition-colors">
                  {faq.question}
                  <span className="text-primary text-xl flex-shrink-0 transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="px-5 pb-5 text-muted-foreground leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related Educational Articles */}
        {service.relatedArticles && service.relatedArticles.length > 0 && (
          <section className="max-w-3xl mx-auto px-4 py-8">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {service.relatedArticles.map((article) => (
                <Link
                  key={article.href}
                  href={article.href}
                  className="block p-5 rounded-2xl border bg-card hover:border-primary hover:shadow-sm transition-all"
                >
                  <span className="font-semibold text-foreground">{article.label}</span>
                  <span className="block text-primary text-sm mt-1">Read more →</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="bg-primary/5 py-12 px-4 text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-bold mb-3">Ready to Get Started?</h2>
            <p className="text-muted-foreground mb-6">
              Call us at{" "}
              <a href="tel:+919322022392" className="text-primary font-semibold">+91 93220 22392</a>{" "}
              or book your appointment online.
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-block bg-primary text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Book Appointment
            </button>
          </div>
        </section>

        <ContactFooter />
      </main>
    </>
  );
}
